import {
  animate,
  AUTO_STYLE,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import {
  SafeHtmlPipe,
  StickyDirective,
  UserTypeService,
  WindowRef,
  QaHooksDirective,
} from '@boels/helpers';
import {
  BoelsProductReference,
  BoelsProductReferenceType,
  BOProduct,
  ProductReference,
  UnitPrice,
} from '@boels/models';
import { CartFacade, ProductFacade } from '@boels/shared-state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { asyncScheduler, combineLatest, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  observeOn,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  AddToCartButtonClass,
  AddToCartButtonComponent,
} from '../add-to-cart-button/add-to-cart-button.component';
import { PriceService } from '../product-price/price.service';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ProductInCartLabelComponent } from '@boels/elements';
import { BoelsSkeletonComponent } from '../skeleton/boels-skeleton.component';

const DEFAULT_DURATION = 300;
const OFFSET_SUMMARY_HEIGHT = 36;

@Component({
  selector: 'boels-product-summary',
  templateUrl: './product-summary.component.html',
  styleUrls: ['./product-summary.component.scss'],
  imports: [
    CommonModule,
    FontAwesomeModule,
    TranslocoModule,
    MatExpansionModule,
    AddToCartButtonComponent,
    StickyDirective,
    ProductInCartLabelComponent,
    SafeHtmlPipe,
    QaHooksDirective,
    BoelsSkeletonComponent,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['productDetails'],
    },
  ],
  animations: [
    trigger('collapse', [
      state('false', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
      state('true', style({ height: '0', visibility: 'hidden' })),
      transition(
        'false <=> true',
        animate(`${DEFAULT_DURATION}ms ease-in-out`),
      ),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
  host: { ngSkipHydration: 'true' },
})
export class ProductSummaryComponent implements OnInit {
  #winRef = inject(WindowRef);
  #priceService = inject(PriceService);
  #productFacade = inject(ProductFacade);
  #userTypeService = inject(UserTypeService);
  #cartFacade = inject(CartFacade);
  #destroyRef = inject(DestroyRef);
  #platformId = inject(PLATFORM_ID);

  summarySection = viewChild<ElementRef<HTMLElement>>('summaryContainer');

  $product = toSignal<BOProduct | undefined>(
    this.#productFacade.getCurrentProduct() || of(undefined),
  );
  $orderEntry = toSignal(
    toObservable(this.$product).pipe(
      switchMap((product) =>
        this.#cartFacade
          .getProductOrderEntry(product)
          .pipe(observeOn(asyncScheduler)),
      ),
    ),
  );
  $isExpanded = signal(false);
  $collapsable = toSignal(this.#setIsCollapsable(), { initialValue: false });
  $unitPrices = signal<UnitPrice[]>([]);
  $label = signal('');
  $mandatoryAccessories = signal<ProductReference[]>([]);
  $isPlatformBrowser = signal(isPlatformBrowser(this.#platformId));

  faCaretUp = faCaretUp;
  faCaretDown = faCaretDown;
  addToCartBtnClass = AddToCartButtonClass;

  ngOnInit(): void {
    this.#updateProductData(this.$product());
    this.#handleUserTypeChange();
  }

  toggle(): void {
    this.$isExpanded.update((value) => !value);
  }

  addToCartLink(): void {
    const addToCart = this.#winRef.document.querySelector(
      'boels-add-to-cart-button',
    );
    addToCart?.getElementsByTagName('button')[0]?.click();
  }

  #updateProductData(product: BOProduct | undefined): void {
    if (!product?.code) return;
    this.$mandatoryAccessories.set(
      product.productReferences?.filter(
        (ref: BoelsProductReference) =>
          ref.referenceType === BoelsProductReferenceType.MANDATORY_HIRE ||
          ref.referenceType === BoelsProductReferenceType.MANDATORY_SALE,
      ) ?? [],
    );
    this.#updatePriceData(product);
  }

  #updatePriceData(product: BOProduct): void {
    this.$unitPrices.set(this.#priceService.getPricesForUserType(product));
    this.$label.set(this.#priceService.getVatLabel());
  }

  #handleUserTypeChange(): void {
    this.#userTypeService
      .onUserTypeChange()
      .pipe(
        tap(() => {
          const product = this.$product();
          if (!product) return;
          this.#updatePriceData(product);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #setIsCollapsable(): Observable<boolean> {
    return combineLatest([
      toObservable(this.summarySection).pipe(filter(Boolean), take(1)),
      this.#winRef.resize$.pipe(debounceTime(50)),
    ]).pipe(
      map(() => this.#calculateIsCollapsable()),
      distinctUntilChanged(),
    );
  }

  #calculateIsCollapsable(): boolean {
    // If already expanded, no need to check
    if (this.$isExpanded()) return true;

    const section = this.summarySection()?.nativeElement;
    const summary = section?.firstElementChild;
    if (!summary) return false;

    const computedStyle = this.#winRef.nativeWindow?.getComputedStyle(summary);
    const additionalHeight = computedStyle
      ? parseFloat(computedStyle.fontSize) +
        parseFloat(computedStyle.paddingBottom)
      : OFFSET_SUMMARY_HEIGHT;

    return section.clientHeight + additionalHeight < summary.clientHeight;
  }
}
